import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import dayjs, { Dayjs } from 'dayjs';
import { LanguageService } from '../services/language.service';

/** One grid cell. `null` days are the leading blanks before the 1st of the month. */
interface DayCell {
  day: Dayjs | null;
  disabled: boolean;
  selected: boolean;
  inRange: boolean;
  isToday: boolean;
}

@Component({
  selector: 'lib-calendar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  template: `
    <div class="w-full">
      <div class="flex items-center justify-between px-1 py-1.5">
        <button
          type="button"
          [attr.aria-label]="'Previous month'"
          class="rounded-md p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100"
          (click)="monthChange.emit(month().subtract(1, 'month'))">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="h-4 w-4">
            <path stroke-linecap="round" stroke-linejoin="round" d="m15 18-6-6 6-6" />
          </svg>
        </button>
        <div class="text-sm font-semibold capitalize text-gray-900 dark:text-slate-100">
          {{ month().format('MMMM YYYY') }}
        </div>
        <button
          type="button"
          [attr.aria-label]="'Next month'"
          class="rounded-md p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100"
          (click)="monthChange.emit(month().add(1, 'month'))">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="h-4 w-4">
            <path stroke-linecap="round" stroke-linejoin="round" d="m9 18 6-6-6-6" />
          </svg>
        </button>
      </div>

      <div class="grid grid-cols-7 gap-y-1 pb-1 text-center text-[11px] font-medium text-gray-400 dark:text-slate-500">
        @for (d of weekdays(); track $index) {
          <div>{{ d }}</div>
        }
      </div>

      <div class="grid grid-cols-7 gap-y-1">
        @for (cell of cells(); track $index) {
          <div class="flex items-center justify-center">
            @if (cell.day; as day) {
              <button type="button" [disabled]="cell.disabled" [class]="cellClass(cell)" (click)="dateSelect.emit(day)">
                {{ day.date() }}
              </button>
            }
          </div>
        }
      </div>
    </div>
  `,
})
export class CalendarComponent {
  readonly month = input.required<Dayjs>();
  readonly selectedDate = input.required<Dayjs>();
  readonly minDate = input<Dayjs>();
  readonly maxDate = input<Dayjs>();
  /** Together with {@link rangeTo}, tints every day of the committed span so the
   * operator can see the whole range across both calendars, not just its edges. */
  readonly rangeFrom = input<Dayjs>();
  readonly rangeTo = input<Dayjs>();

  readonly monthChange = output<Dayjs>();
  readonly dateSelect = output<Dayjs>();

  #lang = inject(LanguageService);

  /** Monday-first headers in the active dayjs locale. 2024-01-01 is a Monday —
   * walking a week from it avoids depending on the `localeData` plugin. The
   * `active()` read is what re-runs the formatting on a locale switch. */
  protected readonly weekdays = computed(() => {
    this.#lang.active();
    return Array.from({ length: 7 }, (_, i) => dayjs('2024-01-01').add(i, 'day').format('dd'));
  });

  protected readonly cells = computed<DayCell[]>(() => {
    this.#lang.active();
    const month = this.month();
    const startOfMonth = month.startOf('month');
    const today = dayjs();
    const out: DayCell[] = [];

    // Leading blanks — dayjs' day() is Sunday-based, the grid is Monday-first.
    const firstDayOfWeek = startOfMonth.day();
    const offset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    for (let i = 0; i < offset; i++) {
      out.push({ day: null, disabled: true, selected: false, inRange: false, isToday: false });
    }

    for (let i = 1; i <= month.endOf('month').date(); i++) {
      const day = startOfMonth.date(i);
      out.push({
        day,
        disabled: this.isDateDisabled(day),
        selected: day.isSame(this.selectedDate(), 'day'),
        inRange: this.isInRange(day),
        isToday: day.isSame(today, 'day'),
      });
    }
    return out;
  });

  isDateDisabled(day: Dayjs): boolean {
    const minDate = this.minDate();
    const maxDate = this.maxDate();
    if (minDate && day.isBefore(minDate, 'day')) return true;
    if (maxDate && day.isAfter(maxDate, 'day')) return true;
    return false;
  }

  private isInRange(day: Dayjs): boolean {
    const from = this.rangeFrom();
    const to = this.rangeTo();
    if (!from || !to) return false;
    return !day.isBefore(from, 'day') && !day.isAfter(to, 'day');
  }

  protected cellClass(cell: DayCell): string {
    const base = 'h-8 w-8 rounded-md text-[13px] transition-colors';
    if (cell.selected) return `${base} bg-primary-500 font-bold text-white`;
    if (cell.disabled) return `${base} cursor-not-allowed text-gray-300 dark:text-slate-600`;
    const today = cell.isToday ? ' ring-1 ring-inset ring-primary-400' : '';
    if (cell.inRange)
      return `${base}${today} bg-primary-500/10 font-medium text-primary-700 hover:bg-primary-500/20 dark:bg-primary-500/20 dark:text-primary-200`;
    return `${base}${today} text-gray-700 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-700`;
  }
}
